/* global G */
import { setKey } from '@gaia/util'
import routeComposition from 'trait/composition/route'
import aclInit from 'lib/sequence/app/acl/init'
import pubSubInit from 'lib/sequence/app/pubsub/connect'
import cartInit from 'lib/sequence/app/init/cart'
import notificationsInit from 'lib/sequence/app/init/notifications'
import bluetoothInit from 'lib/sequence/app/init/bluetooth'
import pinCreatorInit from 'lib/sequence/app/init/pincreator'
import settings from '@tenant/settings'
import appSettings from 'config/app'

/**
 * Guest Access Action
 *
 * This is a pure business logic action, it has no UI
 *
 * Sets the application default route for the current module.
 *
 * @param {Gaia.AppModule.Spec} module - application module
 * @returns {function(): function(...[*]): Promise<*[]>}
 */
const action = module => () => async (...args) => {
  const moduleState = module[G.STATE]
  const route = module[G.ADAPTER][G.ROUTER].restorePoint()
  const sessionState = module[G.ADAPTER][G.SESSION][G.STATE]
  const { module: moduleName, action: actionName, key } = route

  aclInit(module)()
  pubSubInit(module)()

  await cartInit(module)()
  await bluetoothInit(module)()
  // All init sequences that communicate with the notifications adapter (like cart, messages)
  // need to come before this init sequence, because they may have already queried the server
  // to fetch their respective resource (e.g. the currently active cart). We'll check for that
  // in the `G.CREATE` method for each group of the notification adapter and can reuse the result
  // of that query and thus don't need to refetch it
  await notificationsInit(module)()

  await pinCreatorInit(module)()

  // call plugins' login lifecycle method
  await module[G.ADAPTER][G.PLUGIN].login()
  // manually power-caching auto-login request in case the application goes offline before
  // calling it itself. This only has an effect if the user goes offline
  if (!settings.suppressPersistence) {
    const { context, refs } = module[G.ADAPTER][G.SESSION][G.STATE][G.META]
    await module[G.ADAPTER][G.PERSISTENCE].requests.put({
      url: '/api/v1/public/login',
      method: 'GET',
      data: { context, refs },
    })
  }

  setKey([], G.PREV, sessionState) // clearing history stack

  const nextRoute = routeComposition(moduleName, actionName, key)

  const shouldOnboard = settings.showOnboardingWarningConfirmation
      && !sessionState?.[G.SETTINGS]?.global?.onboardingWarningConfirmation?.confirmed

  const { onboarding } = appSettings.defaults

  if (shouldOnboard) {
    setKey(routeComposition(onboarding.module, onboarding.action), G.ROUTE, moduleState)
  } else {
    setKey(nextRoute, G.ROUTE, moduleState)
    key && args.unshift({ key })
  }

  setKey(false, G.ERROR, moduleState)

  return shouldOnboard
    ? [nextRoute, ...args]
    : args
}

export default action
